import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Button,
    MenuItem,
    TextField,
    Stack,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Divider,
    Alert,
    Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ITrip, ITripItem, ITripTraveller, OptionalField, TripOptionalData } from "@interfaces";
import { useAppDispatch } from "src/Redux/store";
import { setItemTravellerSelection } from "src/Redux/Slices/tripPlanner";
import { toursApi } from "src/Api/toursApi";
import { travellerCountForItem } from "src/Utilities/tripPlannerHelpers";

interface Props {
    open: boolean;
    item: ITripItem | null;
    /** The trip's roster of people to choose from. */
    roster: ITrip["travellers"];
    onClose: () => void;
    /** Open the trip-level roster editor (when the roster is empty/insufficient). */
    onManageRoster?: () => void;
}

/**
 * Lightweight renderer for a product's additional/optional booking field.
 * (Same minimal controlled input the trip planner used before — we don't reuse
 * the checkout's OptionalDataField, which has auto-fill side effects.)
 */
const AdditionalField = ({
    field,
    value,
    onChange,
}: {
    field: OptionalField;
    value: string | number;
    onChange: (value: string | number) => void;
}) => {
    const label = field.title || field.commonName || "Detail";
    if (field.type === "select") {
        return (
            <TextField
                select
                fullWidth
                size="small"
                label={label}
                required={field.mandatory}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
            >
                {field.options.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                        {opt}
                    </MenuItem>
                ))}
            </TextField>
        );
    }
    if (field.type === "number") {
        return (
            <TextField
                type="number"
                fullWidth
                size="small"
                label={label}
                required={field.mandatory}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            />
        );
    }
    return (
        <TextField
            fullWidth
            size="small"
            label={label}
            required={field.mandatory}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
        />
    );
};

const travellerLabel = (t: ITripTraveller, index: number): string => {
    const name = `${t.firstName} ${t.lastName}`.trim();
    return name || `Traveller ${index + 1}`;
};

/**
 * Per-product traveller PICKER. Instead of re-typing people, the user ticks
 * which roster travellers are booked onto this product. Each selected person
 * can answer the product's additional/optional fields (kept per-product, keyed
 * by traveller id). The number selected must equal the product's booking
 * quantity (numPax-aware) — Save is gated on it.
 *
 * Works on a local copy; only commits to Redux on Save so Cancel discards.
 */
export const TravellerPickerModal = ({ open, item, roster, onClose, onManageRoster }: Props) => {
    const dispatch = useAppDispatch();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [optionalData, setOptionalData] = useState<Record<string, TripOptionalData>>({});
    const [optionalFields, setOptionalFields] = useState<OptionalField[]>([]);
    const [loadingFields, setLoadingFields] = useState(false);

    const required = item ? travellerCountForItem(item) : 0;

    // Seed local selection + optional data from the item whenever it opens.
    useEffect(() => {
        if (!open || !item) return;
        setSelectedIds([...(item.travellerIds ?? [])]);
        // Deep-copy so local edits don't mutate Redux state.
        const seeded: Record<string, TripOptionalData> = {};
        for (const [id, data] of Object.entries(item.travellerOptionalData ?? {})) {
            seeded[id] = { ...data };
        }
        setOptionalData(seeded);
    }, [open, item]);

    // Fetch the product's additional/optional fields for this fare.
    useEffect(() => {
        if (!open || !item) return;
        let cancelled = false;
        setLoadingFields(true);
        setOptionalFields([]);
        toursApi
            .getBookingDetail(item.productPricesDetailsId)
            .then((resp) => {
                if (cancelled) return;
                const fields = resp?.data?.optionalFields ?? [];
                setOptionalFields(Array.isArray(fields) ? fields : []);
            })
            .catch(() => {
                if (!cancelled) setOptionalFields([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingFields(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, item?.productPricesDetailsId]);

    const selectedCount = selectedIds.length;
    const valid = selectedCount === required;

    const validationMessage = useMemo(() => {
        if (valid) return null;
        if (selectedCount < required) {
            return `Select ${required - selectedCount} more traveller${
                required - selectedCount === 1 ? "" : "s"
            } — this product books ${required}.`;
        }
        return `Too many selected — this product books ${required}. Remove ${
            selectedCount - required
        }.`;
    }, [valid, selectedCount, required]);

    if (!item) return null;

    const toggle = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const updateOptional = (travellerId: string, fieldId: number, value: string | number) => {
        setOptionalData((prev) => ({
            ...prev,
            [travellerId]: { ...(prev[travellerId] ?? {}), [String(fieldId)]: value },
        }));
    };

    const handleSave = () => {
        dispatch(
            setItemTravellerSelection({
                itemId: item.id,
                travellerIds: selectedIds,
                optionalData,
            }),
        );
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 5 }}>
                Who's on this product?
                <Typography variant="body2" color="text.secondary">
                    {item.name} — {selectedCount}/{required} selected
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    aria-label="Close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {roster.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            You haven't added any travellers to this trip yet.
                        </Typography>
                        {onManageRoster && (
                            <Button variant="contained" onClick={onManageRoster}>
                                Add travellers
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Stack spacing={1}>
                        {validationMessage && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                {validationMessage}
                            </Alert>
                        )}
                        {roster.map((t, index) => {
                            const checked = selectedIds.includes(t.id);
                            return (
                                <Box
                                    key={t.id}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: checked ? "primary.main" : "divider",
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => toggle(t.id)}
                                            />
                                        }
                                        label={travellerLabel(t, index)}
                                    />
                                    <Collapse in={checked && optionalFields.length > 0}>
                                        <Box sx={{ pl: 4, pr: 1, pb: 1 }}>
                                            <Divider textAlign="left" sx={{ mb: 1.5 }}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Additional details
                                                </Typography>
                                            </Divider>
                                            {loadingFields ? (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        py: 1,
                                                    }}
                                                >
                                                    <CircularProgress size={20} />
                                                </Box>
                                            ) : (
                                                <Stack spacing={1.5}>
                                                    {optionalFields.map((field) => (
                                                        <AdditionalField
                                                            key={field.optionalFieldId}
                                                            field={field}
                                                            value={
                                                                optionalData[t.id]?.[
                                                                    String(field.optionalFieldId)
                                                                ] ?? ""
                                                            }
                                                            onChange={(value) =>
                                                                updateOptional(
                                                                    t.id,
                                                                    field.optionalFieldId,
                                                                    value,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                        {onManageRoster && (
                            <Button onClick={onManageRoster} sx={{ alignSelf: "flex-start" }}>
                                Manage travellers
                            </Button>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={!valid}>
                    Save selection
                </Button>
            </DialogActions>
        </Dialog>
    );
};
